<template>
  <div
    v-for="position in TOAST_POSITIONS"
    v-show="toastsByPosition(position).length"
    :key="position"
    class="toast-stack"
    :class="`toast-stack--${position}`"
  >
    <transition-group
      v-if="toastsByPosition(position).length"
      name="toast-list"
      tag="div"
      class="toast-stack__list"
    >
      <div
        v-for="(toast, index) in toastsByPosition(position)"
        :key="toast.id"
        class="toast-stack__item"
        :class="{
          'toast-stack__item--oldest': isOldest(index),
          'toast-stack__item--stacked': !isOldest(index),
        }"
        :style="stackItemStyle(index, position)"
      >
        <ToastNotification
          v-bind="toast"
          @close="removeToast(toast.id)"
        />
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/ui/alerts/useToast';
import ToastNotification from '@/components/ui/alerts/ToastNotification.vue';

const TOAST_POSITIONS = [
  'top-left',
  'top-right',
  'top-center',
  'bottom-left',
  'bottom-right',
  'bottom-center',
];

const { toasts, removeToast } = useToast();

const toastsByPosition = (position) => toasts.value
  .filter((toast) => (toast.position || 'top-right') === position);

const isOldest = (index) => index === 0;

const stackItemStyle = (index, position) => ({
  '--stack-index': index,
  '--stack-total': toastsByPosition(position).length,
});
</script>

<style lang="scss" scoped>
$toast-stack-width: min(35.8rem, calc(100vw - 4rem));
$toast-stack-offset: 2rem;
$toast-stack-gap: 1.2rem;
$toast-stack-step: 0.6rem;
$toast-stack-scale-step: 0.03;

.toast-stack {
  position: fixed;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  width: $toast-stack-width;
  pointer-events: none;

  &--top-left {
    top: $toast-stack-offset;
    left: $toast-stack-offset;
  }

  &--top-right {
    top: $toast-stack-offset;
    right: $toast-stack-offset;
  }

  &--top-center {
    top: $toast-stack-offset;
    left: 50%;
    transform: translateX(-50%);
  }

  &--bottom-left {
    bottom: $toast-stack-offset;
    left: $toast-stack-offset;
  }

  &--bottom-right {
    bottom: $toast-stack-offset;
    right: $toast-stack-offset;
  }

  &--bottom-center {
    bottom: $toast-stack-offset;
    left: 50%;
    transform: translateX(-50%);
  }

  &--top-left,
  &--top-right,
  &--top-center {
    @include breakpoint($max-m) {
      .toast-stack__item {
        transform-origin: top center;
      }
    }
  }

  &--bottom-left,
  &--bottom-right,
  &--bottom-center {
    @include breakpoint($max-m) {
      .toast-stack__item {
        transform-origin: bottom center;
      }
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: none;

    @include breakpoint($max-m) {
      position: relative;
    }
  }

  &__item {
    width: 100%;
    pointer-events: auto;

    @include breakpoint($max-m) {
      transform: scale(calc(1 - var(--stack-index) * #{$toast-stack-scale-step}));

      &--oldest {
        position: relative;
        z-index: var(--stack-total);
        pointer-events: auto;
        margin-bottom: calc((var(--stack-total) - 1) * #{$toast-stack-step});
      }

      &--stacked {
        position: absolute;
        left: 0;
        right: 0;
        top: calc(var(--stack-index) * #{$toast-stack-step} * -1);
        z-index: calc(var(--stack-total) - var(--stack-index));
        pointer-events: none;
      }
    }

    @include breakpoint($min-m) {
      position: static;
      margin: 0;
      transform: none;
      pointer-events: auto;
      z-index: auto;
    }
  }

  @include breakpoint($min-m) {
    &__list {
      gap: $toast-stack-gap;
    }
  }
}

.toast-list-enter-active,
.toast-list-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.toast-list-enter-from,
.toast-list-leave-to {
  opacity: 0;
  transform: translateY(1.2rem);
}

.toast-list-leave-active {
  position: absolute;
  width: 100%;
}

.toast-list-move {
  transition: transform 0.3s ease;
}
</style>
